import type { CustomerOverview } from '@vy/api-definition';
import {
  append,
  extract,
  filterBy,
  isNotEmptyArray,
  removeByField,
} from '@vy/utils';
import { useCallback, useDeferredValue, useMemo, useState } from 'react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { CloseButton } from '../../../../components/design/buttons/CloseButton';
import { Checkbox } from '../../../../components/design/checkbox';
import { Input } from '../../../../components/design/Input/Input';
import { SearchIcon } from '../../../../components/icons';
import { withSuspenseBoundary } from '../../../../components/utility/SuspenseBoundary';
import type { NewPlanAssignment } from '../plans-page-types/new-plan-assignment-schema';
import type { PlanAssignmentInput } from '../plans-page-types/plan-assignment-input-schema';
import { useCustomersOverviews } from '../../../../hooks/requests/use-customers-overviews/use-customers-overviews';

type ContractSidebarFirstStepProps = {
  title: string;
};

const AssignPlanStepCustomers: React.FC<ContractSidebarFirstStepProps> = ({ title }) => {
  const { watch, setValue, reset } = useFormContext<PlanAssignmentInput, NewPlanAssignment>();
  const selectedCustomers = watch('selectedCustomers') ?? [];
  const [customersSearchQuery, setCustomersSearchQuery] = useState('');
  const deferredCustomersSearchQuery = useDeferredValue(customersSearchQuery);

  const handleCustomerAdded = useCallback(
    (customer: CustomerOverview) => setValue('selectedCustomers', append(selectedCustomers ?? [], customer)),
    [setValue, selectedCustomers],
  );
  const handleCustomerRemoved = useCallback(
    (customer: CustomerOverview) =>
      setValue('selectedCustomers', removeByField(selectedCustomers ?? [], customer.id, 'id')),
    [setValue, selectedCustomers],
  );
  const handleCustomerToggled = useCallback(
    (customer: CustomerOverview, checked: boolean) =>
      (checked ? handleCustomerAdded : handleCustomerRemoved)(customer),
    [handleCustomerAdded, handleCustomerRemoved],
  );

  const handleClose = () => reset();

  const onCustomersSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomersSearchQuery(event.target.value);
  }, []);

  const isCustomerSelected = useCallback(
    ({ id: customerId }: CustomerOverview) => {
      const selectedCustomersIds = extract(selectedCustomers, 'id');

      return selectedCustomersIds.includes(customerId);
    },
    [selectedCustomers],
  );

  const { data: customerOverviewResponse } = useCustomersOverviews({
    suspense: true,
  });
  const { customers = [] } = customerOverviewResponse ?? {};

  const filteredCustomers = useMemo(
    () => filterBy(customers, deferredCustomersSearchQuery, 'name'),
    [customers, deferredCustomersSearchQuery],
  );

  return (
    <>
      <div className="flex w-full items-center justify-between">
        <div className="flex w-full items-center justify-start">
          <p className="text-xl font-medium">{title}</p>
          {isNotEmptyArray(selectedCustomers) && (
            <p className='text-placeholder before:bg-placeholder relative ml-3 pl-3 text-base before:absolute before:inset-y-1 before:left-0 before:w-[1px] before:content-[""]'>
              {`${selectedCustomers.length} selected`}
            </p>
          )}
        </div>
        <CloseButton onClick={handleClose} />
      </div>
      <div className="relative flex">
        <SearchIcon className="absolute left-8 top-1/2 h-5 w-5 -translate-y-1/2" />
        <Input placeholder="Search a customer" onChange={onCustomersSearch} debounce={false} />
      </div>
      <div className="flex w-full flex-col overflow-y-auto">
        {filteredCustomers.map((customer) => (
          <Checkbox
            key={customer.id}
            onToggle={(event) => handleCustomerToggled(customer, event.target.checked)}
            checked={isCustomerSelected(customer)}
            label={customer.name}
          />
        ))}
      </div>
    </>
  );
};

const AssignPlanStepCustomersWithSuspenseBoundary = withSuspenseBoundary(AssignPlanStepCustomers);
export { AssignPlanStepCustomersWithSuspenseBoundary as AssignPlanStepCustomers };
